import React, { useState, useEffect, lazy, Suspense } from 'react';
import { supabase } from './lib/supabase';
import Nav from './components/Nav';

const HomePage = lazy(() => import('./pages/HomePage'));
const PlayerSearchPage = lazy(() => import('./pages/PlayerSearchPage'));
const TransferPortalPage = lazy(() => import('./pages/TransferPortalPage'));
const RecruitingPage = lazy(() => import('./pages/RecruitingPage'));
const ScoutBoard = lazy(() => import('./pages/ScoutBoard'));
const RecruitBoard = lazy(() => import('./pages/RecruitBoard'));
const MockDraft = lazy(() => import('./pages/MockDraft'));
// const MockDraftV2 = lazy(() => import('./pages/MockDraftV2')); // V2 paused 2026-05-28 — see docs/MOCKDRAFT_V2_BUILD_SPEC.md
const BigBoard = lazy(() => import('./pages/BigBoard'));
const SeasonPredictor = lazy(() => import('./pages/SeasonPredictor'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ProgramModal = lazy(() => import('./modals/ProgramModal'));

const PageFallback = (
  <div className="min-h-screen bg-carbon-950 flex items-center justify-center">
    <p className="text-body text-carbon-300">Loading...</p>
  </div>
);

function App() {
  const [page, setPage] = useState('home');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [board, setBoard] = useState([]);
  const [recruitBoard, setRecruitBoard] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    const loadBoard = async (userId) => {
      const { data, error } = await supabase.from('board_players').select('*').eq('user_id', userId).order('created_at', { ascending: true });
      if (!error) setBoard(data || []);
    };
    const loadRecruitBoard = async (userId) => {
      const { data, error } = await supabase.from('recruit_board').select('*').eq('user_id', userId).order('created_at', { ascending: true });
      if (!error) setRecruitBoard(data || []);
    };
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) { loadBoard(session.user.id); loadRecruitBoard(session.user.id); }
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) { loadBoard(session.user.id); loadRecruitBoard(session.user.id); }
      else { setBoard([]); setRecruitBoard([]); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } });
  };

  const removeFromBoard = async (id) => { await supabase.from('board_players').delete().eq('id', id); setBoard(prev => prev.filter(p => p.id !== id)); };
  const updateNote = async (id, note) => { await supabase.from('board_players').update({ note }).eq('id', id); setBoard(prev => prev.map(p => p.id === id ? { ...p, note } : p)); };
  const updateTag = async (id, tag) => { await supabase.from('board_players').update({ tag }).eq('id', id); setBoard(prev => prev.map(p => p.id === id ? { ...p, tag } : p)); };

  const removeFromRecruitBoard = async (id) => { await supabase.from('recruit_board').delete().eq('id', id); setRecruitBoard(prev => prev.filter(r => r.id !== id)); };
  const updateRecruitNote = async (id, note) => { await supabase.from('recruit_board').update({ note }).eq('id', id); setRecruitBoard(prev => prev.map(r => r.id === id ? { ...r, note } : r)); };
  const updateRecruitTag = async (id, tag) => { await supabase.from('recruit_board').update({ tag }).eq('id', id); setRecruitBoard(prev => prev.map(r => r.id === id ? { ...r, tag } : r)); };

  if (authLoading) return PageFallback;

  let pageEl;
  if (page === 'home') pageEl = <HomePage onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={board} recruitBoard={recruitBoard} />;
  else if (page === 'board') pageEl = <ScoutBoard board={board} onRemove={removeFromBoard} onUpdateNote={updateNote} onUpdateTag={updateTag} onNavigate={setPage} user={user} onSignIn={signInWithGoogle} recruitBoard={recruitBoard} />;
  else if (page === 'recruiting') pageEl = <RecruitingPage onNavigate={setPage} user={user} onSignIn={signInWithGoogle} recruitBoard={recruitBoard} setRecruitBoard={setRecruitBoard} playerBoard={board} />;
  else if (page === 'recruitboard') pageEl = <RecruitBoard board={recruitBoard} onRemove={removeFromRecruitBoard} onUpdateNote={updateRecruitNote} onUpdateTag={updateRecruitTag} onNavigate={setPage} user={user} onSignIn={signInWithGoogle} playerBoard={board} />;
  else if (page === 'portal') pageEl = <TransferPortalPage onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={recruitBoard} setBoard={setRecruitBoard} playerBoard={board} />;
  else if (page === 'draft') pageEl = (
    <div className="min-h-screen bg-carbon-950 text-carbon-200">
      <Nav page="draft" onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={board} recruitBoard={recruitBoard} />
      <MockDraft onNavigate={setPage} user={user} onSignIn={signInWithGoogle} />
    </div>
  );
  /* V2 paused 2026-05-28 — see docs/MOCKDRAFT_V2_BUILD_SPEC.md. To re-enable,
     uncomment this block and the lazy import above.
  else if (page === 'mockdraftv2') pageEl = (
    <div className="min-h-screen bg-carbon-950 text-carbon-200">
      <Nav page="mockdraftv2" onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={board} recruitBoard={recruitBoard} />
      <MockDraftV2 onNavigate={setPage} user={user} onSignIn={signInWithGoogle} />
    </div>
  );
  */
  else if (page === 'bigboard') pageEl = (
    <div className="min-h-screen bg-carbon-950 text-carbon-200">
      <Nav page="bigboard" onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={board} recruitBoard={recruitBoard} />
      <BigBoard user={user} onSignIn={signInWithGoogle} />
    </div>
  );
  else if (page === 'season-predictor') pageEl = <SeasonPredictor onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={board} recruitBoard={recruitBoard} />;
  else if (page === 'admin') pageEl = <AdminDashboard onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={board} recruitBoard={recruitBoard} />;
  else pageEl = <PlayerSearchPage onNavigate={setPage} user={user} onSignIn={signInWithGoogle} board={board} setBoard={setBoard} recruitBoard={recruitBoard} onSelectProgram={setSelectedProgram} />;

  return (
    <>
      <Suspense fallback={PageFallback}>{pageEl}</Suspense>
      {selectedProgram && (
        <Suspense fallback={null}>
          <ProgramModal team={selectedProgram} onClose={() => setSelectedProgram(null)} />
        </Suspense>
      )}
    </>
  );
}

export default App;
